import React from "react";

import Icon from "@mdi/react";
import chroma from "chroma-js";
import classNames from "classnames";
import { useFormContext } from "react-hook-form";

import Switch from "../../form/Switch";

interface OptionCheckboxProps {
  title: string;
  subtitle: string;
  description: string;
  className?: string;
  name: string;
  color: string;
  icon: string;
  multiplier?: number;
  children?: React.ReactNode;
}

function OptionCheckbox({
  title,
  subtitle,
  description,
  className,
  name,
  color,
  icon,
  multiplier,
  children,
}: OptionCheckboxProps): React.JSX.Element {
  const { watch } = useFormContext();

  const value = watch(name);

  const containerClassNames = classNames(
    "w-full rounded-lg border-2 p-3.75 transition-all",
    {
      "border-status-active/40": value,
      "border-transparent bg-muted": !value,
    },
    className,
  );

  const iconClassNames = classNames(
    "rounded-lg p-1.25 transition-all shrink-0",
    {
      "shadow-lg text-primary-foreground shadow": value,
      "shadow-sm bg-card text-muted-foreground": !value,
    },
  );

  return (
    <div
      className={containerClassNames}
      style={{
        backgroundColor: value ? chroma(color).alpha(0.05).hex() : undefined,
        borderColor: value ? chroma(color).alpha(0.3).hex() : undefined,
      }}
    >
      <div className="flex items-center gap-[0.78125rem]">
        <div
          className={iconClassNames}
          style={{
            backgroundColor: value ? color : undefined,
          }}
        >
          <Icon path={icon} size={1.25} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground text-[0.9375rem] font-semibold">
            {title}
          </h3>
          <div className="mt-0.5 flex items-center gap-1.25">
            <span
              className="border-status-active/40 bg-card rounded border px-1 py-0.5 text-[0.703125rem]"
              style={{
                color,
                borderColor: chroma(color).alpha(0.2).hex(),
              }}
            >
              {subtitle}
            </span>
          </div>
        </div>
        <div>
          <Switch
            isCompact
            isNoWrap
            className="mb-0!"
            color={color}
            name={name}
            size="sm"
          />
        </div>
      </div>
      <div className="mt-2.5 flex justify-between">
        <p className="text-muted-foreground text-[0.78125rem] leading-relaxed font-medium">
          {description}
        </p>
        {multiplier !== undefined && (
          <span className="bg-muted text-muted-foreground self-start rounded px-1 py-0.5 font-mono text-[0.703125rem]">
            x{multiplier}
          </span>
        )}
      </div>
      {children ? (
        <div className="border-border mt-2.5 border-t pt-2.5">{children}</div>
      ) : null}
    </div>
  );
}

export default OptionCheckbox;
